// src/features/home/components/ClockInOutWidget.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip,
} from '@mui/material';
import { LoginOutlined, LogoutOutlined, AccessTime, WorkOutline } from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useRegistrations } from '../../tidsregistering/hooks/useRegistrations';
import { useRegistrationCodes } from '../../tidsregistering/hooks/useRegistrationCodes';
import { usePlannedShifts } from '../../tidsregistering/hooks/usePlannedShifts';
import { useDepartments } from '../../departments/hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import {
  useTidsregistreringRegistrationPermissions,
  useTidsregistreringPlannedShiftPermissions,
  useTidsregistreringRegistrationCodePermissions,
} from '../../auth/hooks/usePermissions';
import TimingReasonDialog from './TimingReasonDialog';
import HomeCard from './HomeCard';

const DEFAULT_TOLERANCE = 5;

function timeStrToMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function getRelevantShiftForClockIn(registrations, shifts) {
  const completedCount = registrations.filter((r) => r.start_logged && r.end_logged).length;
  return shifts[completedCount] ?? shifts[shifts.length - 1];
}

function getRelevantShiftForClockOut(openReg, shifts) {
  if (!openReg?.start_time) return shifts[shifts.length - 1];
  // Use start_time (server-localized) not start_logged (UTC) to avoid timezone mismatch.
  const loginMinutes = timeStrToMinutes(openReg.start_time);
  // Only consider shifts that end after the clock-in time — a shift ending before
  // you even clocked in can never be the one you're leaving.
  const eligible = shifts.filter((s) => timeStrToMinutes(s.end_time) > loginMinutes);
  const candidates = eligible.length > 0 ? eligible : shifts;
  return candidates.reduce((best, shift) => {
    const shiftStart = timeStrToMinutes(shift.start_time);
    const bestStart = timeStrToMinutes(best.start_time);
    return Math.abs(shiftStart - loginMinutes) < Math.abs(bestStart - loginMinutes) ? shift : best;
  });
}

export default function ClockInOutWidget({ staffMemberId, departmentId }) {
  const theme = useTheme();
  const { t } = useTranslation(['tidsregistering']);
  const { registrations, fetchForDate, clockIn, clockOut } = useRegistrations();
  const { registrationCodes, fetchForDepartment } = useRegistrationCodes();
  const { plannedShifts, fetchForDepartment: fetchShifts } = usePlannedShifts();
  const { getDepartmentById, currentDepartment } = useDepartments();
  const { showSuccess, showError } = useSnackbar();
  const registrationPermissions = useTidsregistreringRegistrationPermissions();
  const plannedShiftPermissions = useTidsregistreringPlannedShiftPermissions();
  // Clocking in books Normaltid, and an early or late clock uses a reason code
  const { canRead: canReadCodes } = useTidsregistreringRegistrationCodePermissions();

  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContext, setDialogContext] = useState(null);
  const [dialogDeviationMinutes, setDialogDeviationMinutes] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);

  const today = format(new Date(), 'yyyy-MM-dd');
  const toleranceMinutes = currentDepartment?.clock_tolerance_minutes ?? DEFAULT_TOLERANCE;

  const reload = useCallback(() => {
    if (staffMemberId) fetchForDate(staffMemberId, today);
  }, [staffMemberId, today, fetchForDate]);

  useEffect(() => {
    if (staffMemberId && registrationPermissions.canRead) {
      fetchForDate(staffMemberId, today);
      if (canReadCodes) fetchForDepartment();
    }
    if (staffMemberId && plannedShiftPermissions.canRead) {
      fetchShifts(today, today);
    }
  }, [staffMemberId, registrationPermissions.canRead, plannedShiftPermissions.canRead, canReadCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (departmentId) getDepartmentById(departmentId);
  }, [departmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const todaysShifts = plannedShifts
    .filter((s) => s.staff_member_id === staffMemberId && s.is_working)
    .sort((a, b) => a.start_time.localeCompare(b.start_time));

  const openReg = registrations.find((r) => r.start_logged && !r.end_logged);
  const normaltidCode = registrationCodes.find((c) => c.registration_category === 'normal_hours');
  const isClockedIn = !!openReg;

  const doClockIn = useCallback(async (codeId, context) => {
    setBusy(true);
    const payload = {
      registration_code_id: codeId,
      logged_at: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    // Early/late arrival: the registration stays Normaltid; the picked reason
    // only covers the gap to the planned start (mirrors clock-out).
    if ((context === 'early_arrival' || context === 'late_arrival') && normaltidCode && todaysShifts.length > 0) {
      const relevantShift = getRelevantShiftForClockIn(registrations, todaysShifts);
      payload.registration_code_id = normaltidCode.registration_code_id;
      payload.deviation_code_id = codeId;
      payload.deviation_context = context;
      payload.shift_start_time = relevantShift.start_time.slice(0, 5);
    }
    const result = await clockIn(payload);
    setBusy(false);
    if (result.success) { showSuccess(t('tidsregistering:clock_in_out_widget.clock_in_success')); reload(); }
    else showError(result.error ?? t('tidsregistering:clock_in_out_widget.clock_in_error'));
  }, [clockIn, normaltidCode, todaysShifts, registrations, showSuccess, showError, reload, t]);

  const doClockOut = useCallback(async (codeId, context) => {
    if (!openReg) return;
    setBusy(true);
    const now = new Date();
    const payload = { logged_at: now.toISOString() };
    if (codeId && context && todaysShifts.length > 0) {
      const relevantShift = getRelevantShiftForClockOut(openReg, todaysShifts);
      payload.registration_code_id = codeId;
      payload.deviation_context = context;
      payload.shift_end_time = relevantShift.end_time.slice(0, 5);
    }
    const result = await clockOut(openReg.registration_id, payload);
    if (result.success) {
      showSuccess(t('tidsregistering:clock_in_out_widget.clock_out_success'));
      reload();
    } else {
      showError(result.error ?? t('tidsregistering:clock_in_out_widget.clock_out_error'));
    }
    setBusy(false);
  }, [openReg, clockOut, todaysShifts, showSuccess, showError, reload, t]);

  const handleClockIn = () => {
    if (!normaltidCode) { showError(t('tidsregistering:clock_in_out_widget.no_normal_hours_code')); return; }
    if (!todaysShifts.length) {
      setDialogContext('unscheduled_work'); setDialogDeviationMinutes(0); setPendingAction('in'); setDialogOpen(true);
      return;
    }
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const relevantShift = getRelevantShiftForClockIn(registrations, todaysShifts);
    const diff = nowMinutes - timeStrToMinutes(relevantShift.start_time);
    if (diff < -toleranceMinutes) {
      setDialogContext('early_arrival'); setDialogDeviationMinutes(Math.abs(diff)); setPendingAction('in'); setDialogOpen(true);
    } else if (diff > toleranceMinutes) {
      setDialogContext('late_arrival'); setDialogDeviationMinutes(diff); setPendingAction('in'); setDialogOpen(true);
    } else {
      doClockIn(normaltidCode.registration_code_id);
    }
  };

  const handleClockOut = () => {
    if (!openReg) return;
    if (!todaysShifts.length) { doClockOut(); return; }
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const relevantShift = getRelevantShiftForClockOut(openReg, todaysShifts);
    const diff = nowMinutes - timeStrToMinutes(relevantShift.end_time);
    if (diff > toleranceMinutes) {
      setDialogContext('late_departure'); setDialogDeviationMinutes(diff); setPendingAction('out'); setDialogOpen(true);
    } else if (diff < -toleranceMinutes) {
      setDialogContext('early_departure'); setDialogDeviationMinutes(Math.abs(diff)); setPendingAction('out'); setDialogOpen(true);
    } else {
      doClockOut();
    }
  };

  const handleDialogConfirm = (codeId) => {
    setDialogOpen(false);
    if (pendingAction === 'in') doClockIn(codeId, dialogContext); else doClockOut(codeId, dialogContext);
  };

  const handleDialogClose = () => { setDialogOpen(false); setDialogContext(null); setPendingAction(null); };

  const accentColor = isClockedIn ? theme.palette.success.main : theme.palette.primary.main;
  const canActClockInOut = canReadCodes
    && (isClockedIn ? registrationPermissions.canUpdate : registrationPermissions.canCreate);

  return (
    <>
      {/* Arbejdsdag widget — hidden entirely without registration:read */}
      {registrationPermissions.canRead && (
        <HomeCard
          icon={<WorkOutline sx={{ fontSize: 18 }} />}
          title={t('tidsregistering:clock_in_out_widget.title')}
          accentColor={accentColor}
        >
          {/* Status row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: isClockedIn ? 'success.main' : 'text.disabled',
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                {isClockedIn ? t('tidsregistering:clock_in_out_widget.clocked_in_since') : t('tidsregistering:clock_in_out_widget.status')}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {isClockedIn ? openReg.start_time.slice(0, 5) : t('tidsregistering:clock_in_out_widget.not_clocked_in')}
              </Typography>
            </Box>
          </Box>

          {/* Check in/out button — needs registration:create (in) or registration:update (out) */}
          {canActClockInOut && (
            <Button
              variant="contained"
              fullWidth
              size="large"
              color={isClockedIn ? 'error' : 'success'}
              startIcon={
                busy
                  ? <CircularProgress size={18} color="inherit" />
                  : isClockedIn ? <LogoutOutlined /> : <LoginOutlined />
              }
              disabled={busy}
              onClick={isClockedIn ? handleClockOut : handleClockIn}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            >
              {isClockedIn ? t('tidsregistering:clock_in_out_widget.clock_out') : t('tidsregistering:clock_in_out_widget.clock_in')}
            </Button>
          )}
        </HomeCard>
      )}

      {/* Dagens vagter widget — hidden entirely without planned_shift:read */}
      {plannedShiftPermissions.canRead && (
        <HomeCard
          icon={<AccessTime sx={{ fontSize: 18 }} />}
          title={t('tidsregistering:clock_in_out_widget.todays_shifts_title')}
        >
          {todaysShifts.length === 0 ? (
            <Typography variant="body2" color="text.tertiary">
              {t('tidsregistering:clock_in_out_widget.no_shifts_today')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {todaysShifts.map((shift) => {
                const start = shift.start_time.slice(0, 5);
                const end = shift.end_time.slice(0, 5);
                const label = shift.break_minutes > 0
                  ? t('tidsregistering:clock_in_out_widget.shift_label_with_break', { start, end, breakMinutes: shift.break_minutes })
                  : t('tidsregistering:clock_in_out_widget.shift_label', { start, end });
                return (
                  <Chip key={shift.planned_shift_id} label={label} size="small" color="primary" variant="outlined" />
                );
              })}
            </Box>
          )}
        </HomeCard>
      )}

      <TimingReasonDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        context={dialogContext}
        codes={registrationCodes}
        deviationMinutes={dialogDeviationMinutes}
      />
    </>
  );
}
